import axios from "axios";
import { getApiRoute } from "src/services";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";

/** Rails per feed page, and how many tiles each rail opens with. */
export const SECTIONS_PAGE_SIZE = 5;
export const CALENDARS_PER_SECTION = 7;

/**
 * Tiles a desktop rail opens with. Slides are a fixed width, so a wide viewport
 * fits more of them than the mobile seed covers — most of all in a poster rail.
 */
export const DESKTOP_CALENDARS_PER_SECTION = 12;

const SECTIONS_STALE_TIME_MS = 1000 * 60 * 60;

/**
 * Fetches one page of the section feed. A `tag` filter is its own path (sections
 * whose tags include it), taking precedence over `category` on the backend.
 *
 * @param {{ category?: string, tag?: string, page: number, seed?: string, pageSize: number, calendarsPerSection: number }} params
 * @returns {Promise<{ sections: Array<object>, page: number, pageSize: number, hasMore: boolean, seed: string }>}
 */
export async function getSections({ category, tag, page, seed, pageSize, calendarsPerSection }) {
  const response = await axios.get(getApiRoute("home", "GET_SECTIONS"), {
    params: {
      ...(category ? { category } : {}),
      ...(tag ? { tag } : {}),
      ...(seed ? { seed } : {}),
      page,
      pageSize,
      calendarsPerSection,
    },
    withCredentials: true,
  });
  return response.data;
}

export const sectionsQueryKey = (category, tag, pageSize, calendarsPerSection) => [
  "sections",
  category ?? null,
  tag ?? null,
  pageSize,
  calendarsPerSection,
];

/**
 * Infinite feed of section rails. A signed-in request has the user's
 * personalized rails prepended by the backend on page 1, so the caller renders
 * whatever order arrives. Pass `tag` to render a tag-scoped set of rails.
 *
 * @param {{ category?: string, tag?: string, pageSize?: number, calendarsPerSection?: number }} params
 * @param {object} options - Passed through to react-query.
 */
export const useSections = (
  {
    category,
    tag,
    pageSize = SECTIONS_PAGE_SIZE,
    calendarsPerSection = CALENDARS_PER_SECTION,
  } = {},
  options = {},
) => {
  const query = useInfiniteQuery(
    sectionsQueryKey(category, tag, pageSize, calendarsPerSection),
    ({ pageParam = { page: 1 } }) =>
      getSections({ category, tag, page: pageParam.page, seed: pageParam.seed, pageSize, calendarsPerSection }),
    {
      staleTime: SECTIONS_STALE_TIME_MS,
      refetchOnWindowFocus: false,
      // Later pages echo page 1's rotation seed so one scroll session keeps
      // one daily ordering even across the UTC date flip.
      getNextPageParam: (lastPage) =>
        lastPage?.hasMore ? { page: (lastPage.page ?? 0) + 1, seed: lastPage.seed } : undefined,
      ...options,
    },
  );

  const sections = useMemo(
    () => query.data?.pages.flatMap((page) => page.sections ?? []) ?? [],
    [query.data],
  );

  return { ...query, sections };
};
